import React, { useEffect, useState } from "react";
import {
  Phone,
  ChevronDown,
  Menu,
  X,
  Globe,
  MessageCircle,
  ShoppingBag,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useCart } from "../../contexts/CartContext";
import { whatsappUrl, reservationMessage, WHATSAPP_DISPLAY } from "../../lib/whatsapp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const PHONE_DISPLAY = "671 71 23 35";
const PHONE_RAW = "+34671712335";

export default function Header() {
  const { t, lang, setLang, languages } = useLanguage();
  const { count, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: t.nav.home, href: "/#inicio" },
    { label: t.nav.menu, href: "/#carta" },
    { label: t.nav.tienda, href: "/#tienda" },
    { label: t.nav.craft, href: "/#esencia" },
    { label: t.nav.reviews, href: "/#reviews" },
    { label: t.nav.contact, href: "/#contact" },
  ];

  const waReservation = whatsappUrl(reservationMessage(t));

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F6F0]/90 border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo */}
          <a
            href="/#inicio"
            data-testid="site-logo"
            className="flex items-baseline gap-2 group whitespace-nowrap"
          >
            <span
              className={`font-serif-display text-2xl md:text-3xl lg:text-[2rem] tracking-[0.06em] uppercase leading-none transition-colors ${
                scrolled ? "text-[#1A1A1A]" : "text-white drop-shadow-md"
              }`}
            >
              GR A
            </span>
            <span
              className={`font-serif-display text-2xl md:text-3xl lg:text-[2rem] tracking-[0.06em] uppercase italic leading-none transition-colors ${
                scrolled ? "text-[#8B1E0F]" : "text-[#E8A87C] drop-shadow-md"
              }`}
            >
              Cuchillo
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-7">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.href.replace(/[/#]/g, "") || "home"}`}
                className={`text-sm tracking-wide whitespace-nowrap transition-colors relative group ${
                  scrolled
                    ? "text-[#1A1A1A] hover:text-[#8B1E0F]"
                    : "text-white hover:text-[#E8A87C] drop-shadow"
                }`}
              >
                {item.label}
                <span
                  className={`absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-300 ${
                    scrolled ? "bg-[#8B1E0F]" : "bg-[#E8A87C]"
                  }`}
                />
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-switcher-trigger"
                className={`flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors px-2 py-2 ${
                  scrolled
                    ? "text-[#1A1A1A] hover:text-[#8B1E0F]"
                    : "text-white hover:text-[#E8A87C] drop-shadow"
                }`}
              >
                <Globe className="h-4 w-4" />
                <span>{lang}</span>
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#F9F6F0] border-[#E5E0D8] rounded-none min-w-[160px]"
              >
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`cursor-pointer rounded-none text-sm tracking-wide ${
                      lang === l.code
                        ? "text-[#8B1E0F] font-semibold"
                        : "text-[#1A1A1A]"
                    } focus:bg-[#E5E0D8] focus:text-[#1A1A1A]`}
                  >
                    <span className="font-mono text-xs mr-3">{l.label}</span>
                    {l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cart icon */}
            <button
              onClick={openCart}
              data-testid="header-cart-btn"
              className={`relative p-2.5 transition-colors ${
                scrolled
                  ? "text-[#1A1A1A] hover:text-[#8B1E0F]"
                  : "text-white hover:text-[#E8A87C] drop-shadow"
              }`}
              aria-label="cart"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#8B1E0F] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>

            {/* WhatsApp Reserve CTA */}
            <a
              href={waReservation}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-cta"
              className="hidden md:inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-5 py-3 text-xs tracking-[0.15em] uppercase font-semibold whitespace-nowrap transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              {t.whatsapp.buttonLabel}
            </a>

            {/* Phone CTA */}
            <a
              href={`tel:${PHONE_RAW}`}
              data-testid="header-phone-cta"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.15em] uppercase font-semibold whitespace-nowrap transition-colors ${
                scrolled
                  ? "bg-[#8B1E0F] hover:bg-[#6A160A] text-white"
                  : "bg-white/10 backdrop-blur-md border border-white text-white hover:bg-white hover:text-[#1A1A1A]"
              }`}
            >
              <Phone className="h-3.5 w-3.5" />
              {PHONE_DISPLAY}
            </a>

            {/* Mobile menu toggle */}
            <button
              data-testid="mobile-menu-toggle"
              className={`xl:hidden p-2 ${
                scrolled ? "text-[#1A1A1A]" : "text-white drop-shadow"
              }`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-nav-panel"
          className="xl:hidden bg-[#F9F6F0] border-t border-[#E5E0D8]"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`mobile-nav-link-${item.href.replace(/[/#]/g, "") || "home"}`}
                onClick={() => setMobileOpen(false)}
                className="text-base text-[#1A1A1A] hover:text-[#8B1E0F]"
              >
                {item.label}
              </a>
            ))}
            <a
              href={waReservation}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 text-xs uppercase tracking-[0.15em] font-semibold"
            >
              <MessageCircle className="h-4 w-4" />
              {t.whatsapp.buttonLabel} · {WHATSAPP_DISPLAY}
            </a>
            <a
              href={`tel:${PHONE_RAW}`}
              data-testid="mobile-phone-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#8B1E0F] text-white py-3 text-xs uppercase tracking-[0.15em] font-semibold"
            >
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
